import { jwtDecode } from 'jwt-decode'
import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

import { decryptData, encryptData, generateKey } from '../utils'

interface SecureStorageContextType {
  secret: string | null
  handleGetSecret: () => void
  handleClearSecret: () => void
  handleSaveSecret: (secret: string) => void
}

interface DecodedToken {
  __s: string
}

const SecureStorageContext = createContext<SecureStorageContextType | undefined>(undefined)

const Provider = ({ children }: { children: ReactNode }) => {
  const [secret, setSecret] = useState<string | null>(null)

  const handleSaveSecret = async (secret: string) => {
    const secretDecoded: DecodedToken = jwtDecode(secret)
    const secretLocalDb = secretDecoded.__s.split(':')[0]
    const secretCredential = secretDecoded.__s.split(':')[1]
    sessionStorage.setItem('__ldbssc', secretCredential)

    const key = await generateKey(secretCredential)
    const encryptedSecret = await encryptData(secretLocalDb, key)
    sessionStorage.setItem('__ldbs', encryptedSecret)
    setSecret(secretLocalDb)
  }

  const handleGetSecret = async () => {
    const encryptedSecret = sessionStorage.getItem('__ldbs')
    const secretCredential = sessionStorage.getItem('__ldbssc')

    if (!encryptedSecret || !secretCredential) return null

    const key = await generateKey(secretCredential)
    return await decryptData(encryptedSecret, key)
  }

  const handleClearSecret = () => {
    setSecret(null)
  }

  useEffect(() => {
    handleGetSecret().then(setSecret)
  }, [])

  return <SecureStorageContext.Provider value={{ secret, handleSaveSecret, handleGetSecret, handleClearSecret }}>{children}</SecureStorageContext.Provider>
}

export const useSecureStorage = () => {
  const context = useContext(SecureStorageContext)

  if (!context) {
    throw new Error('useSecureStorage must be used within a SecureStorageProvider')
  }

  return context
}

const SecureStorageProvider = {
  Provider,
  Consumer: SecureStorageContext.Consumer
}

export default SecureStorageProvider
